import { Search } from 'lucide-react'
import { Select } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { fetchRobots } from '@/api/robots.api'
import { fetchUnits } from '@/api/units.api'
import type { DateRangeValue } from '@/components/ui/date-range-picker'
import { useAtomValue } from 'jotai'
import { currentUserAtom } from '@/store/auth.atom'
import dayjs from 'dayjs'
import { FilterFormWrapper } from '@/components/FilterFormWrapper'

export interface RobotFeedbackFilterValues {
    robotId: string | undefined
    robotName?: string
    unitId: string | undefined
    unitName?: string
    dateRange: DateRangeValue | undefined
}

interface RobotFeedbackFilterPanelProps {
    values: RobotFeedbackFilterValues
    onChange: (values: RobotFeedbackFilterValues) => void
    onSearch: () => void
}

export function RobotFeedbackFilterPanel({
    values,
    onChange,
    onSearch,
}: RobotFeedbackFilterPanelProps) {
    const currentUser = useAtomValue(currentUserAtom)
    const showUnitColumn = currentUser?.userType === 1
    const resolvedUnitId = showUnitColumn ? values.unitId : currentUser?.unitId

    function set<TKey extends keyof RobotFeedbackFilterValues>(
        key: TKey,
        value: RobotFeedbackFilterValues[TKey],
    ) {
        onChange({ ...values, [key]: value })
    }

    const defaultDateRange: DateRangeValue = {
        from: dayjs().startOf('month').toDate(),
        to: dayjs().endOf('day').millisecond(0).toDate(),
    }

    return (
        <FilterFormWrapper
            onSearch={onSearch}
            className="rounded-xl border border-gray-300 bg-white px-6 py-5"
        >
            <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex min-w-[200px] flex-1 flex-col gap-1">
                        <span className="text-sm font-medium">Ngày thực hiện</span>
                        <DateRangePicker
                            value={values.dateRange ?? defaultDateRange}
                            onChange={(v) => set('dateRange', v)}
                        />
                    </div>

                    {showUnitColumn && (
                        <div className="flex min-w-[200px] flex-1 flex-col gap-1">
                            <span className="text-sm font-medium">Đơn vị</span>
                            <Select
                                cacheKey="robot-feedback-filter-unit-select"
                                initialPage={0}
                                lazy
                                placeholder="Tất cả đơn vị"
                                allowClear
                                value={values.unitId ?? undefined}
                                className="w-full"
                                enableRemoteSearch
                                allLabel="Tất cả đơn vị"
                                initialOptions={
                                    values.unitId && values.unitName
                                        ? [{ value: values.unitId, label: values.unitName }]
                                        : []
                                }
                                loadOptions={async ({ page, signal, searchText }) => {
                                    const res = await fetchUnits(
                                        page,
                                        10,
                                        { keySearch: searchText?.trim() || undefined },
                                        signal,
                                    )
                                    const arr = Array.isArray(res.items) ? res.items : []
                                    const hasNextPage = arr.length >= 10

                                    return {
                                        options: arr.map((u: any) => ({
                                            value: u.id,
                                            label: u.name,
                                        })),
                                        hasNextPage,
                                        nextPage: hasNextPage ? page + 1 : undefined,
                                    }
                                }}
                                onLazyValueChange={(unitId, option) => {
                                    onChange({
                                        ...values,
                                        unitId: unitId,
                                        unitName: option?.label ?? undefined,
                                        robotId: undefined,
                                        robotName: undefined,
                                    })
                                }}
                            />
                        </div>
                    )}

                    <div className="flex min-w-[200px] flex-1 flex-col gap-1">
                        <span className="text-sm font-medium">Robot</span>
                        <Select
                            cacheKey={`robot-select-robot-feedback-filter-${resolvedUnitId ?? 'all'}`}
                            initialPage={0}
                            lazy
                            placeholder="Tất cả robot"
                            allowClear
                            value={values.robotId ?? undefined}
                            className="w-full"
                            enableRemoteSearch
                            allLabel="Tất cả robot"
                            initialOptions={
                                values.robotId && values.robotName
                                    ? [{ value: values.robotId, label: values.robotName }]
                                    : []
                            }
                            loadOptions={async ({ page, signal, searchText }) => {
                                const res = await fetchRobots(
                                    page,
                                    10,
                                    {
                                        robotId: searchText?.trim() || undefined,
                                        unitId: resolvedUnitId,
                                    },
                                    signal,
                                )
                                const arr = Array.isArray(res.items) ? res.items : []
                                const hasNextPage = arr.length >= 10

                                return {
                                    options: arr.map((u: any) => ({
                                        value: u.id,
                                        label: u.name,
                                    })),
                                    hasNextPage,
                                    nextPage: hasNextPage ? page + 1 : undefined,
                                }
                            }}
                            onLazyValueChange={(robotId, option) => {
                                onChange({
                                    ...values,
                                    robotId: robotId,
                                    robotName: option?.label ? String(option.label) : undefined,
                                })
                            }}
                        />
                    </div>

                    <Button
                        variant="outline"
                        size="md"
                        onClick={onSearch}
                        className="border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10"
                    >
                        <Search size={16} />
                        Tìm kiếm
                    </Button>
                </div>
            </div>
        </FilterFormWrapper>
    )
}
